/**
 * API client.
 *
 * Money crosses the wire as decimal strings, never JSON numbers: a balance in
 * minor units can exceed 2^53, and `JSON.parse` would silently round it. The
 * server sends strings; this module turns them into `bigint` and nothing in the
 * UI ever holds an amount as a `number`.
 */

const BASE = import.meta.env.VITE_API_URL ?? '/api';

/**
 * Session token store.
 *
 * Kept as an external store with subscribers rather than a bare module
 * variable. React cannot observe a plain variable, so components that read the
 * token would not re-render when it changed — sign-in would appear to do
 * nothing until a full page reload, and a 401-triggered sign-out would leave
 * stale authenticated UI on screen. `useSession` below subscribes properly.
 */
let token: string | null = localStorage.getItem('session');
const listeners = new Set<() => void>();

export function setToken(next: string | null): void {
  if (token === next) return;
  token = next;
  if (next) localStorage.setItem('session', next);
  else localStorage.removeItem('session');
  for (const notify of listeners) notify();
}

export function getToken(): string | null {
  return token;
}

export function subscribeToToken(onChange: () => void): () => void {
  listeners.add(onChange);
  return () => listeners.delete(onChange);
}

export function hasToken(): boolean {
  return token !== null;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers);
  // Only when there is actually a body. Declaring application/json on a
  // bodyless POST — publish, finalise, sign-out — makes the server reject the
  // request for having an empty body, which surfaces as an opaque failure on
  // a button that looks like it should just work.
  if (init.body !== undefined) headers.set('content-type', 'application/json');
  if (token) headers.set('authorization', `Bearer ${token}`);

  const res = await fetch(`${BASE}${path}`, { ...init, headers });
  const text = await res.text();
  const body: unknown = text ? JSON.parse(text) : null;

  if (!res.ok) {
    if (res.status === 401) setToken(null);
    const message =
      body && typeof body === 'object' && 'error' in body ? String(body.error) : `request failed (${res.status})`;
    throw new ApiError(res.status, message, body);
  }
  return body as T;
}

// --------------------------------------------------------------------- types

export type Side = 'YES' | 'NO';
export type MarketStatus = 'DRAFT' | 'OPEN' | 'CLOSED' | 'PROPOSED' | 'RESOLVED' | 'VOIDED' | 'SETTLED';

export interface MarketSummary {
  id: string;
  slug: string;
  question: string;
  status: MarketStatus;
  closes_at: string;
  last_price: number | null;
  open_interest: number;
}

export interface MarketDetail extends MarketSummary {
  criterion: string;
  resolution_source: string;
  outcome: Side | 'VOID' | null;
  opens_at: string;
  resolves_by: string;
  dispute_ends_at: string | null;
  fee_bps: number;
}

export interface Level {
  price: number;
  qty: number;
}
export interface Book {
  YES: Level[];
  NO: Level[];
}

export interface Trade {
  price: number;
  qty: number;
  kind: 'MINT' | 'DIRECT' | 'BURN';
  created_at: string;
}

export interface Me {
  userId: string;
  displayName: string;
  isAdmin: boolean;
  cash: bigint;
  escrow: bigint;
}

export interface AdminMarket extends MarketDetail {
  open_disputes: number;
  proposed_outcome: 'YES' | 'NO' | 'VOID' | null;
}

export type ResolutionSpec =
  | { source: 'coinbase'; product: string; at: string; comparison: string; value: number }
  | {
      source: 'open-meteo';
      latitude: number;
      longitude: number;
      variable: string;
      date: string;
      timezone: string;
      place?: string;
      comparison: string;
      value: number;
    }
  | { source: 'equity-eod'; symbol: string; date: string; comparison: string; value: number };

export interface ResolutionQueueRow {
  id: string;
  question: string;
  status: string;
  closes_at: string;
  resolution_spec: ResolutionSpec | null;
  explanation: string | null;
  last_determination: string | null;
  last_evidence: string | null;
  last_attempt_at: string | null;
  last_error: string | null;
  attempts: number;
}

export interface PerformancePoint {
  marketId: string;
  question: string;
  outcome: string | null;
  settledAt: string | null;
  /** Minor units, as a decimal string. Signed. */
  pnl: string;
  cumulative: string;
}

export interface LeaderboardRow {
  userId: string;
  displayName: string;
  realisedPnl: string;
  marketsSettled: number;
  wins: number;
  losses: number;
  winRate: number | null;
  rank: number;
}

export interface AdminUser {
  id: string;
  email: string;
  display_name: string;
  is_admin: boolean;
  created_at: string;
  suspended_at: string | null;
  suspension_reason: string | null;
  cash: string;
  escrow: string;
  open_positions: number;
  open_orders: number;
  trades: number;
  last_seen: string | null;
}

export interface Position {
  market_id: string;
  slug: string;
  question: string;
  side: Side;
  qty: number;
  reserved: number;
  last_price: number | null;
  status: MarketStatus;
}

export interface OpenOrder {
  id: string;
  market_id: string;
  side: Side;
  price: number;
  qty: number;
  filled_qty: number;
  state: string;
  created_at: string;
}

export type PlaceResult =
  | { accepted: true; orderId: string; fills: Array<{ price: number; qty: number; kind: string }> }
  | { accepted: false; reason: string };

// ------------------------------------------------------------------ requests

export const api = {
  /** Ask for a one-time sign-in link. Never reveals whether the address exists. */
  requestLink: (email: string) =>
    request<{ sent: true; expiresInMinutes: number; devLink?: string }>('/auth/request-link', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),

  redeem: (token: string) =>
    request<{ token: string; userId: string; isNew: boolean }>('/auth/redeem', {
      method: 'POST',
      body: JSON.stringify({ token }),
    }),

  signOut: () => request<{ signedOut: boolean }>('/auth/sign-out', { method: 'POST' }),

  me: async (): Promise<Me> => {
    const raw = await request<{
      userId: string;
      displayName: string;
      isAdmin: boolean;
      cash: string;
      escrow: string;
    }>('/me');
    return { ...raw, cash: BigInt(raw.cash), escrow: BigInt(raw.escrow) };
  },

  markets: () => request<{ markets: MarketSummary[] }>('/markets').then((r) => r.markets),

  market: (id: string) => request<{ market: MarketDetail; book: Book }>(`/markets/${id}`),

  book: (id: string) =>
    request<{ book: Book; lastPrice: number | null; openInterest: number }>(`/markets/${id}/book`),

  trades: (id: string) => request<{ trades: Trade[] }>(`/markets/${id}/trades`).then((r) => r.trades),

  positions: () => request<{ positions: Position[] }>('/me/positions').then((r) => r.positions),

  orders: () => request<{ orders: OpenOrder[] }>('/me/orders').then((r) => r.orders),

  placeOrder: (
    marketId: string,
    body: { direction: 'BUY' | 'SELL'; side: Side; price: number; qty: number; tif?: 'GTC' | 'IOC' | 'FOK' },
    idempotencyKey?: string,
  ) =>
    request<PlaceResult>(`/markets/${marketId}/orders`, {
      method: 'POST',
      body: JSON.stringify(body),
      headers: idempotencyKey ? { 'idempotency-key': idempotencyKey } : undefined,
    }),

  cancelOrder: (marketId: string, orderId: string) =>
    request<{ cancelled: boolean }>(`/markets/${marketId}/orders/${orderId}`, { method: 'DELETE' }),

  // ------------------------------------------------------------------ admin

  adminMarkets: () =>
    request<{ markets: AdminMarket[] }>('/admin/markets').then((r) => r.markets),

  createMarket: (body: {
    question: string;
    criterion: string;
    resolutionSource: string;
    opensAt: string;
    closesAt: string;
    resolvesBy: string;
    /** Present makes the market resolve itself; absent keeps it manual. */
    resolutionSpec?: ResolutionSpec;
  }) => request<{ id: string }>('/admin/markets', { method: 'POST', body: JSON.stringify(body) }),

  publishMarket: (id: string) =>
    request<{ status: MarketStatus }>(`/admin/markets/${id}/publish`, { method: 'POST' }),

  closeMarket: (id: string, force = false) =>
    request<{ status: MarketStatus }>(`/admin/markets/${id}/close`, {
      method: 'POST',
      body: JSON.stringify({ force }),
    }),

  proposeResolution: (id: string, outcome: 'YES' | 'NO' | 'VOID', evidence: string) =>
    request<{ status: MarketStatus; disputeEndsAt: number | null }>(`/admin/markets/${id}/propose`, {
      method: 'POST',
      body: JSON.stringify({ outcome, evidence }),
    }),

  finaliseMarket: (id: string) =>
    request<{ status: MarketStatus; outcome: string | null }>(`/admin/markets/${id}/finalise`, {
      method: 'POST',
    }),

  voidMarket: (id: string, reason: string) =>
    request<{ status: MarketStatus }>(`/admin/markets/${id}/void`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  adminUsers: (q?: string) =>
    request<{ users: AdminUser[] }>(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ''}`).then((r) => r.users),

  adjustCredits: (userId: string, amount: bigint, reason: string) =>
    request<{ adjusted: boolean }>(`/admin/users/${userId}/credits`, {
      method: 'POST',
      body: JSON.stringify({ amount: amount.toString(), reason }),
    }),

  setSuspended: (userId: string, suspended: boolean, reason?: string) =>
    request<{ suspended: boolean }>(`/admin/users/${userId}/suspend`, {
      method: 'POST',
      body: JSON.stringify({ suspended, reason }),
    }),

  setAdmin: (userId: string, isAdmin: boolean) =>
    request<{ isAdmin: boolean }>(`/admin/users/${userId}/admin`, {
      method: 'POST',
      body: JSON.stringify({ isAdmin }),
    }),

  revokeSessions: (userId: string) =>
    request<{ revoked: number }>(`/admin/users/${userId}/sessions/revoke`, { method: 'POST' }),

  register: (body: { email: string; password: string; displayName: string; acceptedTerms: true }) =>
    request<{ sent: boolean; expiresInMinutes: number; devLink?: string }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  signInWithPassword: (email: string, password: string) =>
    request<{ token: string; userId: string }>('/auth/password', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),

  setPassword: (password: string) =>
    request<{ set: boolean }>('/me/password', { method: 'POST', body: JSON.stringify({ password }) }),

  summary: () =>
    request<{
      cash: string;
      escrow: string;
      openAtCost: string;
      realisedPnl: string;
      marketsSettled: number;
      wins: number;
      losses: number;
      winRate: number | null;
      bestMarket: string;
      worstMarket: string;
    }>('/me/summary'),

  performance: () =>
    request<{ points: PerformancePoint[] }>('/me/performance').then((r) => r.points),

  leaderboard: () =>
    request<{ top: LeaderboardRow[]; me: LeaderboardRow | null; minimumSettledMarkets: number }>('/leaderboard'),

  previewResolution: (spec: ResolutionSpec) =>
    request<{
      ok: boolean;
      explanation: string;
      describe?: string;
      requestUrl?: string;
      wouldResolve?: string;
      error?: string;
    }>('/admin/resolution-preview', { method: 'POST', body: JSON.stringify(spec) }),

  resolutionQueue: () =>
    request<{ markets: ResolutionQueueRow[]; autoResolveEnabled: boolean; equityKeyConfigured: boolean }>(
      '/admin/resolution-queue',
    ),

  invite: (emails: string[], note?: string) =>
    request<{ invited: number }>('/admin/invites', {
      method: 'POST',
      body: JSON.stringify({ emails, note }),
    }),
};
